import { useCallback, useEffect } from 'react'
import { useProductPageStore } from '../hooks/useProductPageStore'

/**
 * This is a custom component that bootstraps Shopify's Accelerated Checkout
 * https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout/
 *
 * When the page loads, we clone the payment button into an entrypoint to be displayed underneath our Add to Cart button.
 * After selecting a variant, the cloned button is updated with the selected variant ID.
 *
 * We repeatedly clone the button as a shadow element to ensure that each time the page is re-rendered and we move the button to the entrypoint,
 * we don't lose it on the next page render.
 * @returns ExpressCheckout component
 */
export const ExpressCheckout: React.FC = () => {
  const { selectedVariant } = useProductPageStore()

  const entrypointId = 'express-checkout-entrypoint'
  const sourceId = 'express-checkout-form'
  const sourceInputId = 'express-checkout-variant-id-input'
  const clonedFormInputId = 'express-checkout-variant-id-input-cloned'

  const cloneAndAppendForm = useCallback(() => {
    const entrypoint = document.getElementById(entrypointId)
    const sourceContainer = document.getElementById(sourceId)
    const sourceContainerInput = document.getElementById(sourceInputId)

    if (!entrypoint || !sourceContainer || !sourceContainerInput) {
      return console.error('Cannot clone ExpressCheckout form', {
        entrypointExists: !!entrypoint,
        sourceContainerExists: !!sourceContainer,
        sourceContainerInputExists: !!sourceContainerInput,
      })
    }

    // Avoid cloning if we've already cloned the container
    if (entrypoint.querySelector('#express-checkout-form-cloned')) {
      return
    }

    const clonedContainer = sourceContainer.cloneNode(true) as HTMLDivElement
    clonedContainer.classList.remove('hidden')
    clonedContainer.classList.add('w-full')
    clonedContainer.id = 'express-checkout-form-cloned'

    const clonedContainerInput = clonedContainer.querySelector(
      `#${sourceInputId}`,
    )
    if (!clonedContainerInput) {
      return console.error('Cannot find cloned input element')
    }

    clonedContainerInput.id = clonedFormInputId
    entrypoint.appendChild(clonedContainer)
  }, [])

  const updateClonedForm = useCallback(() => {
    const clonedInput = document.getElementById(clonedFormInputId)

    if (!clonedInput) {
      console.error('Cloned Express Checkout Variant ID not found')
      return
    }

    if (selectedVariant?.available) {
      clonedInput.removeAttribute('disabled')
      clonedInput.setAttribute('value', selectedVariant.id.toString())
    } else {
      clonedInput.removeAttribute('value')
      clonedInput.setAttribute('disabled', 'true')
    }
  }, [selectedVariant])

  useEffect(() => {
    cloneAndAppendForm()
  }, [cloneAndAppendForm])

  useEffect(() => {
    updateClonedForm()
  }, [updateClonedForm])

  return (
    <div
      className="flex flex-row gap-4 mt-2"
      id={entrypointId}
      data-testid={entrypointId}
    ></div>
  )
}
